import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MyIdentityPage from './pages/MyIdentityPage';
import DashboardPage from './pages/DashboardPage';
import IdentitiesPage from './pages/IdentitiesPage';
import RolesPage from './pages/RolesPage';
import EntitlementsPage from './pages/EntitlementsPage';
import ApplicationsPage from './pages/ApplicationsPage';
import AccessRequestsPage from './pages/AccessRequestsPage';
import ApprovalsPage from './pages/ApprovalsPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import AISearchPage from './pages/AISearchPage';
import SSOCallbackPage from './pages/SSOCallbackPage';
import CertificationsPage from './pages/CertificationsPage';
import ReportsPage from './pages/ReportsPage';
import SodPage from './pages/SodPage';
import ProvisioningTicketsPage from './pages/ProvisioningTicketsPage';
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';
import AppAccountsPage from './pages/AppAccountsPage';
import RoleMiningPage from './pages/RoleMiningPage';
import LifecyclePage from './pages/LifecyclePage';
import JitAccessPage from './pages/JitAccessPage';
import Identity360Page from './pages/Identity360Page';

function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

// Client-side RBAC is a UX guard only — all endpoints enforce server-side check_permission().
const GOVERNANCE_ROLES = ['admin', 'governance_admin', 'certification_manager', 'sod_manager', 'provisioning_manager', 'report_viewer'];

function AdminRoute({ children, roles = GOVERNANCE_ROLES }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const userRole = user?.admin_role || 'viewer';
  if (!roles.includes(userRole)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<SSOCallbackPage />} />
      <Route path="/" element={<ProtectedRoute><MyIdentityPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<AdminRoute><DashboardPage /></AdminRoute>} />
      <Route path="/identities" element={<AdminRoute><IdentitiesPage /></AdminRoute>} />
      <Route path="/identities/:identityId" element={<AdminRoute><Identity360Page /></AdminRoute>} />
      <Route path="/roles" element={<AdminRoute><RolesPage /></AdminRoute>} />
      <Route path="/entitlements" element={<AdminRoute><EntitlementsPage /></AdminRoute>} />
      <Route path="/applications" element={<AdminRoute><ApplicationsPage /></AdminRoute>} />
      <Route path="/app-accounts/:appId" element={<AdminRoute><AppAccountsPage /></AdminRoute>} />
      <Route path="/access-requests" element={<ProtectedRoute><AccessRequestsPage /></ProtectedRoute>} />
      <Route path="/approvals" element={<ProtectedRoute><ApprovalsPage /></ProtectedRoute>} />
      <Route path="/certifications" element={<AdminRoute roles={['admin', 'governance_admin', 'certification_manager']}><CertificationsPage /></AdminRoute>} />
      <Route path="/reports" element={<AdminRoute roles={['admin', 'governance_admin', 'report_viewer']}><ReportsPage /></AdminRoute>} />
      <Route path="/sod" element={<AdminRoute roles={['admin', 'governance_admin', 'sod_manager']}><SodPage /></AdminRoute>} />
      <Route path="/role-mining" element={<AdminRoute><RoleMiningPage /></AdminRoute>} />
      <Route path="/provisioning-tickets" element={<AdminRoute roles={['admin', 'governance_admin', 'provisioning_manager']}><ProvisioningTicketsPage /></AdminRoute>} />
      <Route path="/lifecycle" element={<AdminRoute><LifecyclePage /></AdminRoute>} />
      <Route path="/jit-access" element={<ProtectedRoute><JitAccessPage /></ProtectedRoute>} />
      <Route path="/notification-preferences" element={<ProtectedRoute><NotificationPreferencesPage /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute roles={['admin']}><AdminSettingsPage /></AdminRoute>} />
      <Route path="/ai-search" element={<AdminRoute><AISearchPage /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
